export type T = (key: string, params?: Record<string, unknown>) => string;

/** English copy for the sign-in failures, keyed as the catalogue keys them; the fallback when no translator is passed. */
export const signInFailureEnglish: Record<string, string> = {
  'auth.failure.cancelled.title': 'Sign-in was cancelled',
  'auth.failure.cancelled.description': 'No problem — sign in again whenever you are ready.',
  'auth.failure.expiredLink.title': 'This sign-in link has expired',
  'auth.failure.expiredLink.description': 'Sign-in links work once and only for a few minutes. Start again to continue.',
  'auth.failure.unreachable.title': "We can't reach the sign-in service",
  'auth.failure.unreachable.description': 'Check your connection and try again. If it keeps happening, contact your administrator.',
  'auth.failure.unavailable.title': 'Sign-in is temporarily unavailable',
  'auth.failure.unavailable.description': 'The identity service did not respond as expected. Try again in a moment.',
  'auth.failure.invalidGrant.title': 'Your sign-in could not be completed',
  'auth.failure.invalidGrant.description': 'The sign-in took too long or was already used. Start again to continue.',
  'auth.failure.misconfigured.title': 'Sign-in is not set up correctly',
  'auth.failure.misconfigured.description': 'This Zagru One installation cannot start a sign-in. Your administrator can fix this.',
  'auth.failure.generic.title': 'Sign-in did not complete',
  'auth.failure.generic.description': 'Start again to continue. If it keeps happening, contact your administrator.',
};

const englishAuthT: T = (key) => signInFailureEnglish[key] ?? key;

function failureKind(code: string | null | undefined): string {
  switch (code) {
    case 'access_denied':
      return 'cancelled';
    case 'client.invalid_state':
    case 'client.invalid_callback':
      return 'expiredLink';
    case 'client.discovery_failed':
    case 'client.token_request_failed':
      return 'unreachable';
    case 'temporarily_unavailable':
    case 'server_error':
      return 'unavailable';
    case 'invalid_grant':
      return 'invalidGrant';
    case 'unauthorized_client':
    case 'invalid_client':
    case 'invalid_request':
    case 'invalid_scope':
    case 'client.missing_issuer':
      return 'misconfigured';
    default:
      return 'generic';
  }
}

/**
 * Sign-in failures in the user's terms. The OIDC error code is kept for "Technical details"; the sentence never
 * repeats it.
 */
export function signInFailureCopy(code: string | null | undefined, t: T = englishAuthT): { title: string; description: string } {
  const kind = failureKind(code);
  return { title: t(`auth.failure.${kind}.title`), description: t(`auth.failure.${kind}.description`) };
}
